import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { ChevronRight } from "lucide-react";

const SECTIONS = [
  { id: "mentions-legales", label: "Mentions légales" },
  { id: "cgu", label: "Conditions générales d'utilisation" },
  { id: "cgv", label: "Conditions générales de vente" },
  { id: "confidentialite", label: "Politique de confidentialité (RGPD)" },
  { id: "cookies", label: "Cookies" },
  { id: "propriete-intellectuelle", label: "Propriété intellectuelle" },
  { id: "droit-applicable", label: "Droit applicable" },
  { id: "responsabilite", label: "Responsabilité & intermédiaire technique" },
];

const LAST_UPDATED = "27 février 2026";

export default function Legal() {
  const [active, setActive] = useState("mentions-legales");
  const { hash } = useLocation();

  useEffect(() => {
    if (hash) {
      const id = hash.replace("#", "");
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
        setActive(id);
      }
    }
  }, [hash]);

  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) setActive(e.target.id);
        });
      },
      { rootMargin: "-20% 0px -70% 0px" }
    );
    SECTIONS.forEach((s) => {
      const el = document.getElementById(s.id);
      if (el) obs.observe(el);
    });
    return () => obs.disconnect();
  }, []);

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
    setActive(id);
    window.history.replaceState(null, "", `#${id}`);
  };

  return (
    <div className="container mx-auto px-4 md:px-8 py-12 md:py-16 max-w-6xl">
      <div className="mb-10 md:mb-14">
        <p className="overline mb-3">Informations légales</p>
        <h1 className="font-display text-4xl md:text-5xl font-black tracking-tighter text-ink">
          Mentions légales, CGU, CGV<br />
          <span className="text-brand">& Politique de confidentialité</span>
        </h1>
        <p className="mt-4 text-subtle text-sm">Dernière mise à jour : {LAST_UPDATED}</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-10 lg:gap-14">
        {/* SIDEBAR */}
        <aside className="lg:sticky lg:top-24 lg:self-start border border-border bg-surface/50 p-1">
          <nav aria-label="Sommaire" className="flex flex-col" data-testid="legal-sidebar">
            {SECTIONS.map((s) => (
              <button
                key={s.id}
                onClick={() => scrollTo(s.id)}
                className={`text-left text-sm px-4 py-3 border-l-2 transition-colors ${
                  active === s.id ? "border-brand bg-white text-ink font-semibold" : "border-transparent text-subtle hover:text-ink hover:bg-white"
                }`}
                data-testid={`legal-nav-${s.id}`}
              >
                <span className="flex items-center justify-between gap-2">
                  {s.label}
                  <ChevronRight size={14} className={active === s.id ? "text-brand" : ""} />
                </span>
              </button>
            ))}
          </nav>
        </aside>

        {/* CONTENT */}
        <article className="space-y-16 text-ink leading-relaxed">
          {/* MENTIONS LEGALES */}
          <Section id="mentions-legales" title="1. Mentions légales">
            <p>Conformément aux dispositions des articles 6-III et 19 de la loi n° 2004-575 du 21 juin 2004 pour la Confiance dans l'économie numérique (LCEN), il est porté à la connaissance des utilisateurs du site BypassGo les informations suivantes.</p>

            <Sub title="Éditeur du site">
              <ul className="list-disc pl-6 space-y-1">
                <li><strong>Nom du site :</strong> BypassGo</li>
                <li><strong>Éditeur :</strong> exploitation en nom propre par une personne physique (micro-entrepreneur / activité en cours de formalisation).</li>
                <li><strong>Identité de l'éditeur :</strong> nom et prénom communiqués sur simple demande à l'adresse ci-dessous.</li>
                <li><strong>Contact :</strong> <a href="mailto:contact@bypassgo.fr" className="text-brand underline">contact@bypassgo.fr</a></li>
                <li><strong>Adresse postale :</strong> non publiée. L'adresse postale de l'éditeur est communiquée sur demande motivée à l'adresse de contact ci-dessus, conformément à l'article 6-III-1 b) de la LCEN pour les personnes physiques.</li>
                <li><strong>Directeur de la publication :</strong> l'éditeur du site.</li>
              </ul>
            </Sub>

            <Sub title="Hébergement">
              <p>Le site BypassGo est hébergé par <strong>Emergent</strong> (plateforme d'hébergement applicatif) — infrastructure Cloud sur serveurs situés au sein de l'Union européenne. Toute demande relative à l'hébergement peut être adressée à l'éditeur qui la transmettra à l'hébergeur.</p>
            </Sub>

            <Sub title="Prestataire de paiement">
              <p>Les paiements en ligne sont opérés par <strong>Stripe Payments Europe, Ltd.</strong> — 1 Grand Canal Street Lower, Grand Canal Dock, Dublin, Irlande. Stripe est agréé en tant qu'établissement de monnaie électronique.</p>
            </Sub>
          </Section>

          {/* CGU */}
          <Section id="cgu" title="2. Conditions générales d'utilisation (CGU)">
            <Sub title="2.1 Objet et description du service">
              <p>BypassGo est une <strong>plateforme technique</strong> permettant :</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>à ses utilisateurs de prendre un ticket à distance auprès d'un établissement partenaire et de suivre leur position dans la file d'attente en temps réel ;</li>
                <li>aux établissements de créer et gérer une file d'attente en temps réel (ouverture, fermeture, appel des tickets, suivi des présences).</li>
              </ul>
              <p>BypassGo agit exclusivement comme <strong>intermédiaire technique</strong>. La plateforme ne fournit pas elle-même les prestations délivrées par les établissements partenaires.</p>
            </Sub>

            <Sub title="2.2 Acceptation des CGU">
              <p>L'utilisation du service implique l'acceptation sans réserve des présentes CGU. À défaut d'acceptation, l'utilisateur doit renoncer à utiliser la plateforme.</p>
            </Sub>

            <Sub title="2.3 Accès et compte">
              <p>La création d'un compte utilisateur ou établissement est nécessaire pour accéder à certaines fonctionnalités. L'utilisateur s'engage à fournir des informations exactes, complètes et à jour, et à préserver la confidentialité de ses identifiants.</p>
            </Sub>

            <Sub title="2.4 Obligations des utilisateurs">
              <ul className="list-disc pl-6 space-y-1">
                <li>fournir des informations exactes lors de l'inscription et de la prise de ticket ;</li>
                <li>utiliser le service conformément à sa destination et dans le respect des lois en vigueur ;</li>
                <li>ne pas porter atteinte au service (saturation, contournement, automatisation, spam) ni aux autres utilisateurs ;</li>
                <li>ne pas créer de comptes multiples dans un but frauduleux ;</li>
                <li>se présenter auprès de l'établissement lorsque le ticket est appelé.</li>
              </ul>
            </Sub>

            <Sub title="2.5 Obligations des établissements">
              <ul className="list-disc pl-6 space-y-1">
                <li>gérer leur file d'attente avec sérieux, diligence et équité entre les utilisateurs ;</li>
                <li>assumer <strong>l'entière responsabilité</strong> de la qualité des prestations fournies, des délais réels d'attente, des ouvertures et fermetures de file, ainsi que des litiges qui pourraient naître avec les utilisateurs ;</li>
                <li>respecter la législation applicable à leur activité (commerciale, sanitaire, administrative, etc.) ;</li>
                <li>traiter les tickets à distance avec la même priorité contractuelle que celle annoncée ;</li>
                <li>ne pas refuser arbitrairement de servir un utilisateur porteur d'un ticket valide.</li>
              </ul>
            </Sub>

            <Sub title="2.6 Limitation de responsabilité">
              <p>BypassGo fournit un service <strong>&laquo; en l'état &raquo;</strong> et ne saurait être tenu responsable :</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>de l'exactitude des temps d'attente estimés (fournis à titre purement indicatif et dépendant de l'activité en temps réel de l'établissement) ;</li>
                <li>de la disponibilité effective de l'établissement (horaires réels, fermeture exceptionnelle, retard, absence, panne) ;</li>
                <li>de la qualité ou de la conformité de la prestation réalisée par l'établissement ;</li>
                <li>des différends commerciaux, litiges ou réclamations entre un utilisateur et un établissement ;</li>
                <li>des interruptions de service liées à la maintenance, aux mises à jour ou à des causes extérieures (force majeure, panne d'infrastructure tierce, attaque informatique).</li>
              </ul>
              <p>BypassGo est une <strong>plateforme technique d'intermédiation</strong>. À ce titre, la plateforme n'est pas partie au contrat de prestation conclu directement entre l'utilisateur et l'établissement.</p>
            </Sub>

            <Sub title="2.7 Suspension et résiliation">
              <p>BypassGo se réserve le droit, sans préavis et sans indemnité, de suspendre ou supprimer tout compte :</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>en cas de violation des présentes CGU ;</li>
                <li>en cas d'usage frauduleux, abusif ou détourné du service ;</li>
                <li>en cas d'impayé d'un abonnement établissement ;</li>
                <li>en cas de signalement sérieux par un autre utilisateur ou une autorité compétente.</li>
              </ul>
            </Sub>

            <Sub title="2.8 Évolution du service">
              <p>BypassGo peut faire évoluer, modifier ou interrompre tout ou partie du service à tout moment. Les utilisateurs seront informés des modifications substantielles des CGU par tout moyen approprié.</p>
            </Sub>
          </Section>

          {/* CGV */}
          <Section id="cgv" title="3. Conditions générales de vente (CGV)">
            <Sub title="3.1 Modèle économique">
              <ul className="list-disc pl-6 space-y-1">
                <li><strong>Visiteurs :</strong> le service est <strong>entièrement gratuit, sans paiement de ticket, sans abonnement et sans limitation</strong>. Aucune évolution payante n'est prévue côté visiteur.</li>
                <li><strong>Établissements :</strong> <strong>gratuité totale jusqu'au 31 décembre 2026 inclus</strong> pour l'ouverture du service, puis <strong>abonnement de 9,90 €/mois TTC — sans engagement</strong> à compter du 1ᵉʳ janvier 2027. La création d'une file d'attente supplémentaire (au-delà de la première incluse dans l'abonnement) est facturée <strong>4,90 € TTC</strong> en paiement ponctuel.</li>
              </ul>
              <p>Les prix sont indiqués en euros, toutes taxes comprises le cas échéant. L'éditeur étant actuellement exploité en nom propre, la TVA peut ne pas être applicable (article 293 B du CGI — franchise en base de TVA, auquel cas la mention &laquo; TVA non applicable, art. 293 B du CGI &raquo; est ajoutée sur la facture).</p>
            </Sub>

            <Sub title="3.2 Paiement sécurisé (établissement)">
              <p>Les paiements d'abonnement établissement sont encaissés via <strong>Stripe Payments Europe, Ltd.</strong> Les données bancaires sont collectées et traitées directement par Stripe sur ses propres systèmes certifiés <strong>PCI-DSS niveau 1</strong>. <strong>BypassGo ne stocke aucune donnée de carte bancaire sur ses serveurs.</strong></p>
            </Sub>

            <Sub title="3.3 Confirmation d'inscription visiteur">
              <p>La prise d'un ticket par un visiteur ne donne lieu à aucune transaction financière. Le ticket est ajouté immédiatement à la file d'attente de l'établissement choisi et apparaît dans l'espace personnel du visiteur.</p>
            </Sub>

            <Sub title="3.4 Abonnement établissement">
              <p>L'abonnement établissement à <strong>9,90 €/mois TTC — sans engagement</strong> est activé à compter du <strong>1ᵉʳ janvier 2027</strong>, à l'issue de la période de gratuité offerte pour l'ouverture du service (jusqu'au 31 décembre 2026 inclus). Il est facturé automatiquement chaque mois à date anniversaire de la souscription. L'établissement peut résilier à tout moment depuis son espace. La résiliation prend effet à la fin du mois en cours ; aucun remboursement prorata temporis n'est dû.</p>
            </Sub>

            <Sub title="3.5 Absence de garantie de résultat">
              <p><strong>La prise d'un ticket ne garantit en aucun cas une prestation immédiate ou dans un délai déterminé.</strong> Elle garantit uniquement l'inscription à la file d'attente de l'établissement. Le temps d'attente effectif dépend exclusivement de l'activité de l'établissement et échappe au contrôle de BypassGo.</p>
            </Sub>

            <Sub title="3.6 Facturation">
              <p>Pour les établissements, une facture mensuelle est mise à disposition à compter de l'activation de l'abonnement, conformément aux obligations légales.</p>
            </Sub>

            <Sub title="3.7 Usages strictement interdits">
              <p>L'utilisateur et l'établissement s'engagent à n'utiliser la plateforme BypassGo qu'à des fins <strong>légales, légitimes et conformes aux bonnes mœurs</strong>. Sont notamment strictement interdits :</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>l'usage à des fins illégales ou délictueuses (notamment <strong>trafic ou vente de stupéfiants</strong>, vente de produits contrefaits, vente d'armes, recel, contournement de la législation fiscale ou douanière, etc.) ;</li>
                <li>l'utilisation d'un nom d'établissement contenant des termes <strong>vulgaires, racistes, discriminatoires, antisémites, homophobes ou incitant à la haine</strong> ; un filtre automatique bloque la création de tels comptes ;</li>
                <li>l'organisation de files d'attente destinées à des activités <strong>portant atteinte à l'ordre public, à la dignité humaine ou aux droits des tiers</strong> ;</li>
                <li>toute tentative de <strong>fraude, contournement</strong> des mécanismes de paiement, d'usurpation d'identité ou de manipulation des files d'attente.</li>
              </ul>
              <p className="mt-2">Tout manquement entraîne la <strong>suspension immédiate du compte sans préavis ni remboursement</strong>, et peut faire l'objet d'un signalement aux autorités compétentes conformément à la législation française et européenne.</p>
            </Sub>
          </Section>

          {/* CONFIDENTIALITE */}
          <Section id="confidentialite" title="4. Politique de confidentialité (RGPD)">
            <p>BypassGo respecte le Règlement (UE) 2016/679 du 27 avril 2016 (RGPD) et la loi Informatique et Libertés modifiée. La présente politique décrit les traitements de données personnelles mis en œuvre sur la plateforme.</p>

            <Sub title="4.1 Responsable de traitement">
              <p>Le responsable de traitement est l'éditeur du site BypassGo. Contact : <a href="mailto:contact@bypassgo.fr" className="text-brand underline">contact@bypassgo.fr</a>.</p>
            </Sub>

            <Sub title="4.2 Données collectées">
              <ul className="list-disc pl-6 space-y-1">
                <li><strong>Utilisateurs :</strong> nom, prénom, adresse email, mot de passe (hashé), historique des tickets (identifiant d'établissement, numéro, horodatage, statut).</li>
                <li><strong>Établissements :</strong> raison sociale, type d'activité, adresse postale, code postal, ville, téléphone, email professionnel, mot de passe (hashé), historique des tickets appelés, statut d'abonnement.</li>
                <li><strong>Paiement :</strong> identifiant de session Stripe, montant, devise, statut de paiement. Aucune donnée de carte n'est collectée ni stockée par BypassGo.</li>
                <li><strong>Techniques :</strong> journaux de connexion, adresse IP (stockée à des fins de sécurité pendant une durée limitée).</li>
              </ul>
            </Sub>

            <Sub title="4.3 Finalités du traitement">
              <ul className="list-disc pl-6 space-y-1">
                <li>exploitation du service (gestion des comptes, des files d'attente, appel des tickets) ;</li>
                <li>exécution des paiements et gestion des abonnements ;</li>
                <li>communication relative à l'utilisation du service (emails transactionnels) ;</li>
                <li>sécurité, lutte contre la fraude et conformité légale ;</li>
                <li>statistiques agrégées et anonymes d'utilisation (pas de profilage individuel).</li>
              </ul>
            </Sub>

            <Sub title="4.4 Base légale">
              <p>Les traitements ont pour base légale <strong>l'exécution du contrat</strong> liant BypassGo à l'utilisateur (article 6.1.b du RGPD) ainsi que, pour la sécurité et la conformité, <strong>l'intérêt légitime</strong> du responsable de traitement (article 6.1.f) et le respect <strong>d'obligations légales</strong> (article 6.1.c).</p>
            </Sub>

            <Sub title="4.5 Destinataires">
              <p>Les données sont destinées à l'éditeur et à ses sous-traitants strictement nécessaires :</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>hébergeur (Emergent / UE) — stockage sécurisé ;</li>
                <li>prestataire de paiement (Stripe) — traitement des paiements ;</li>
                <li>pour les tickets affichés à un établissement : uniquement les informations strictement nécessaires à la file (prénom et initiale du nom pour identification comptoir).</li>
              </ul>
              <p>Aucune donnée n'est cédée, louée ou vendue à des tiers à des fins commerciales.</p>
            </Sub>

            <Sub title="4.6 Durée de conservation">
              <ul className="list-disc pl-6 space-y-1">
                <li>données de compte utilisateur : durée d'activité du compte, puis <strong>3 ans</strong> d'inactivité avant suppression automatique ;</li>
                <li>données de compte établissement : durée du contrat + <strong>5 ans</strong> pour les obligations comptables ;</li>
                <li>données de paiement : <strong>10 ans</strong> pour les obligations comptables et fiscales (article L.123-22 du Code de commerce) ;</li>
                <li>tickets consommés / annulés : <strong>13 mois</strong> à titre statistique, puis suppression ou anonymisation ;</li>
                <li>journaux techniques : <strong>1 an</strong> maximum.</li>
              </ul>
            </Sub>

            <Sub title="4.7 Droits des personnes concernées">
              <p>Conformément aux articles 15 à 22 du RGPD, tout utilisateur dispose des droits suivants :</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>droit d'<strong>accès</strong> à ses données ;</li>
                <li>droit de <strong>rectification</strong> des données inexactes ;</li>
                <li>droit à l'<strong>effacement</strong> (&laquo; droit à l'oubli &raquo;) sous réserve des obligations légales ;</li>
                <li>droit à la <strong>limitation</strong> du traitement ;</li>
                <li>droit à la <strong>portabilité</strong> des données ;</li>
                <li>droit d'<strong>opposition</strong> ;</li>
                <li>droit de définir des directives relatives au sort des données après le décès ;</li>
                <li>droit d'introduire une réclamation auprès de la <strong>CNIL</strong> (<a href="https://www.cnil.fr" className="text-brand underline" target="_blank" rel="noreferrer">www.cnil.fr</a>).</li>
              </ul>
              <p>Ces droits peuvent être exercés à tout moment par courriel à <a href="mailto:contact@bypassgo.fr" className="text-brand underline">contact@bypassgo.fr</a>, en justifiant de son identité. Une réponse est apportée sous un délai maximal d'un mois.</p>
            </Sub>

            <Sub title="4.8 Sécurité">
              <p>BypassGo met en œuvre les mesures techniques et organisationnelles appropriées pour garantir la sécurité des données :</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>chiffrement des communications en HTTPS/TLS ;</li>
                <li>hachage des mots de passe (bcrypt) ;</li>
                <li>tokens d'authentification signés (JWT) à durée limitée ;</li>
                <li>anonymisation des écrans publics (initiales uniquement) ;</li>
                <li>journalisation des accès et limitation des privilèges ;</li>
                <li>sauvegardes régulières.</li>
              </ul>
            </Sub>

            <Sub title="4.9 Transferts hors Union européenne">
              <p>Les données sont hébergées dans l'Union européenne. Aucun transfert hors UE n'est réalisé, à l'exception des opérations de paiement traitées par Stripe (Irlande, UE) conformément à ses propres engagements contractuels.</p>
            </Sub>
          </Section>

          {/* COOKIES */}
          <Section id="cookies" title="5. Cookies et traceurs">
            <p>BypassGo utilise exclusivement un <strong>stockage local (localStorage) strictement nécessaire</strong> au fonctionnement du service :</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>token d'authentification JWT, pour vous maintenir connecté ;</li>
              <li>préférences d'interface éventuelles (thème, langue).</li>
            </ul>
            <p><strong>Aucun cookie publicitaire, aucun traceur marketing, aucun traceur de mesure d'audience tiers</strong> n'est utilisé. Ces traceurs étant strictement nécessaires au service demandé par l'utilisateur, ils sont exemptés de consentement au sens de l'article 82 de la loi Informatique et Libertés (recommandation CNIL).</p>
            <p>L'utilisateur peut à tout moment supprimer les données stockées localement via les paramètres de son navigateur ; cela entraînera sa déconnexion.</p>
          </Section>

          {/* PROPRIETE INTELLECTUELLE */}
          <Section id="propriete-intellectuelle" title="6. Propriété intellectuelle">
            <p>L'ensemble des éléments du site BypassGo — notamment la dénomination &laquo; BypassGo &raquo;, le logo, la charte graphique, l'interface, les textes, les illustrations, le code source, la base de données et l'architecture technique — est <strong>protégé par le droit d'auteur, le droit des marques, et le droit des bases de données</strong>.</p>
            <p>Toute reproduction, représentation, modification, publication, adaptation, diffusion ou exploitation, totale ou partielle, sous quelque forme et par quelque moyen que ce soit, est <strong>strictement interdite</strong> sans autorisation écrite préalable de l'éditeur, sous peine de poursuites au titre de la contrefaçon (articles L.335-2 et suivants du Code de la propriété intellectuelle).</p>
            <p>Les marques et logos des établissements partenaires affichés sur la plateforme demeurent la propriété exclusive de leurs titulaires respectifs.</p>
          </Section>

          {/* DROIT APPLICABLE */}
          <Section id="droit-applicable" title="7. Droit applicable et juridiction">
            <p>Les présentes mentions, CGU, CGV et politique de confidentialité sont soumises <strong>au droit français</strong>.</p>
            <p>En cas de litige, les parties rechercheront une solution amiable avant toute action contentieuse. À défaut, et sous réserve des dispositions légales impératives applicables aux consommateurs, <strong>les tribunaux français seront seuls compétents</strong>, la juridiction compétente étant celle du domicile du défendeur pour les litiges avec un consommateur, et celle du siège de l'éditeur pour les litiges avec un professionnel.</p>
            <p><strong>Médiation de la consommation :</strong> conformément à l'article L.612-1 du Code de la consommation, le consommateur peut recourir gratuitement à un médiateur de la consommation en vue de la résolution amiable d'un litige. L'éditeur communiquera les coordonnées du médiateur compétent sur simple demande.</p>
          </Section>

          {/* RESPONSABILITE */}
          <Section id="responsabilite" title="8. BypassGo, intermédiaire technique">
            <div className="border-2 border-ink p-6 bg-surface/50">
              <p className="font-semibold text-ink">Clause essentielle</p>
              <p className="mt-3">BypassGo est exclusivement une <strong>plateforme technique d'intermédiation</strong> entre les utilisateurs et les établissements. BypassGo :</p>
              <ul className="list-disc pl-6 space-y-1 mt-3">
                <li>n'est <strong>pas partie</strong> au contrat de prestation conclu entre l'utilisateur et l'établissement ;</li>
                <li>ne fournit <strong>aucune</strong> prestation matérielle (coiffure, soin, consultation, réparation, etc.) ;</li>
                <li>ne <strong>garantit pas</strong> la qualité, la régularité, les délais, la sécurité ni la conformité des prestations fournies par les établissements ;</li>
                <li>ne saurait être tenu responsable des <strong>retards, annulations, fermetures, défaillances, litiges, dommages directs ou indirects</strong> pouvant survenir lors de l'exécution d'une prestation par un établissement.</li>
              </ul>
              <p className="mt-4">Les <strong>établissements sont seuls responsables</strong> des prestations qu'ils délivrent, de leur qualité, de leurs délais, de leurs obligations légales, fiscales et sanitaires, ainsi que des litiges qui pourraient en résulter avec les utilisateurs.</p>
              <p className="mt-4">Tout litige relatif à une prestation doit être réglé directement entre l'utilisateur et l'établissement concerné. BypassGo pourra toutefois fournir, sur demande écrite motivée, les éléments techniques en sa possession (horodatage, statut du ticket) strictement nécessaires à la résolution du litige.</p>
            </div>

            <Sub title="Force majeure">
              <p>BypassGo ne saurait être tenu responsable des manquements à ses obligations résultant d'un événement de force majeure au sens de l'article 1218 du Code civil, incluant notamment : panne généralisée d'infrastructure tierce, cyberattaque, catastrophe naturelle, acte des autorités publiques, grève, interruption du prestataire d'hébergement ou de paiement.</p>
            </Sub>
          </Section>

          <div className="border-t border-border pt-8 text-sm text-subtle">
            <p>Pour toute question relative aux présentes, contactez-nous à <a href="mailto:contact@bypassgo.fr" className="text-brand underline">contact@bypassgo.fr</a>.</p>
            <p className="mt-2">© {new Date().getFullYear()} BypassGo · Tous droits réservés.</p>
            <p className="mt-4">
              <Link to="/" className="text-brand font-medium">← Retour à l'accueil</Link>
            </p>
          </div>
        </article>
      </div>
    </div>
  );
}

function Section({ id, title, children }) {
  return (
    <section id={id} className="scroll-mt-24" data-testid={`legal-section-${id}`}>
      <h2 className="font-display text-3xl md:text-4xl font-black tracking-tighter text-ink border-b border-border pb-4">{title}</h2>
      <div className="mt-6 space-y-5 text-subtle">{children}</div>
    </section>
  );
}

function Sub({ title, children }) {
  return (
    <div className="space-y-3">
      <h3 className="font-display text-xl font-bold text-ink mt-6">{title}</h3>
      {children}
    </div>
  );
}
